// /client/src/components/ConversationManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';

const ConversationManager = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/twilio/conversations', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setConversations(data.conversations || []);
      } else {
        setError(data.error || 'Failed to fetch conversations');
      }
    } catch (err) {
      setError('Failed to fetch conversations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchByPhone = async () => {
    if (!searchPhone.trim()) {
      fetchConversations();
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const encoded = encodeURIComponent(searchPhone.trim());
      const response = await fetch(`/api/twilio/conversations/by-phone/${encoded}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setConversations(data.conversations || []);
        if (data.conversations.length === 0) {
          setSuccess(`No conversations found for ${data.phoneNumber}`);
        }
      } else {
        setError(data.error || 'Search failed');
      }
    } catch (err) {
      setError('Search failed');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (sid) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/twilio/conversations/${sid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Conversation deleted successfully');
        setConversations(prev => prev.filter(c => c.sid !== sid));
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Failed to delete conversation');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const deleteSelected = async () => {
    if (selectedConversations.length === 0) {
      setError('No conversations selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedConversations.length} conversation(s)? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setError('');
    let deleted = 0;
    let failed = 0;

    for (const sid of selectedConversations) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/twilio/conversations/${sid}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.success) {
          deleted++;
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
      }
    }

    setSuccess(`Deleted ${deleted} conversation(s)${failed > 0 ? `, ${failed} failed` : ''}`);
    setSelectedConversations([]);
    fetchConversations();
    setDeleting(false);
  };

  const deleteAllConversations = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL Twilio conversations. Are you absolutely sure?')) {
      return;
    }
    
    if (!window.confirm('This is your last chance to cancel. Delete ALL conversations?')) {
      return;
    }

    setDeleting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/twilio/conversations/cleanup/all?confirm=yes', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Deleted ${data.deleted} of ${data.totalFound} conversations`);
        setConversations([]);
      } else {
        setError(data.error || 'Cleanup failed');
      }
    } catch (err) {
      setError('Cleanup failed');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const deleteOldConversations = async (days) => {
    if (!window.confirm(`Delete all conversations older than ${days} days?`)) {
      return;
    }

    setDeleting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/twilio/conversations/cleanup/old?days=${days}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Deleted ${data.deleted} conversation(s) older than ${days} days`);
        fetchConversations();
      } else {
        setError(data.error || 'Cleanup failed');
      }
    } catch (err) {
      setError('Cleanup failed');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (sid) => {
    setSelectedConversations(prev => 
      prev.includes(sid) 
        ? prev.filter(s => s !== sid)
        : [...prev, sid]
    );
  };

  const selectAll = () => {
    if (selectedConversations.length === conversations.length) {
      setSelectedConversations([]);
    } else {
      setSelectedConversations(conversations.map(c => c.sid));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-4 p-0 container'>
            <div className="is-flex is-justify-content-center">
              <div className="column is-10-tablet is-8-desktop">
                
                <button 
                  className="button is-small is-text mb-4"
                  onClick={() => navigate(-1)}
                >
                  ← Back
                </button>

                <div className="mb-5">
                  <h1 className='title is-3'>Twilio Conversation Manager</h1>
                  <p className='subtitle is-6'>
                    Manage and clean up Twilio conversations that may cause message routing issues
                  </p>
                </div>

                {error && (
                  <div className="notification is-danger is-light">
                    <button className="delete" onClick={() => setError('')}></button>
                    {error}
                  </div>
                )}

                {success && (
                  <div className="notification is-success is-light">
                    <button className="delete" onClick={() => setSuccess('')}></button>
                    {success}
                  </div>
                )}

                {/* Search and Actions */}
                <div className="box">
                  <div className="field has-addons">
                    <div className="control is-expanded">
                      <input
                        className="input"
                        type="text"
                        placeholder="Search by phone number (e.g., 5551234567)"
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchByPhone()}
                      />
                    </div>
                    <div className="control">
                      <button 
                        className="button is-info"
                        onClick={searchByPhone}
                      >
                        Search
                      </button>
                    </div>
                    <div className="control">
                      <button 
                        className="button is-light"
                        onClick={() => { setSearchPhone(''); fetchConversations(); }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="buttons mt-4">
                    <button 
                      className="button is-small is-primary"
                      onClick={fetchConversations}
                      disabled={loading}
                    >
                      Refresh
                    </button>
                    <button 
                      className="button is-small is-warning"
                      onClick={() => deleteOldConversations(7)}
                      disabled={deleting}
                    >
                      Delete Older Than 7 Days
                    </button>
                    <button 
                      className="button is-small is-warning"
                      onClick={() => deleteOldConversations(1)}
                      disabled={deleting}
                    >
                      Delete Older Than 1 Day
                    </button>
                    <button 
                      className="button is-small is-danger"
                      onClick={deleteAllConversations}
                      disabled={deleting}
                    >
                      Delete All
                    </button>
                  </div>
                </div>

                {/* Conversations Table */}
                <div className="box">
                  {loading ? (
                    <div className="has-text-centered py-5">
                      <span className="icon is-large">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                      </span>
                      <p className="mt-3">Loading conversations...</p>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="has-text-centered py-5">
                      <p className="has-text-grey">No conversations found</p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 is-flex is-justify-content-space-between is-align-items-center">
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={selectedConversations.length === conversations.length && conversations.length > 0}
                            onChange={selectAll}
                          />
                          {' '}Select All ({conversations.length})
                        </label>
                        
                        {selectedConversations.length > 0 && (
                          <button 
                            className="button is-small is-danger"
                            onClick={deleteSelected}
                            disabled={deleting}
                          >
                            Delete Selected ({selectedConversations.length})
                          </button>
                        )}
                      </div>

                      <div className="table-container">
                        <table className="table is-fullwidth is-hoverable is-striped">
                          <thead>
                            <tr>
                              <th style={{ width: '40px' }}></th>
                              <th>Name</th>
                              <th>State</th>
                              <th>Created</th>
                              <th>Age</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {conversations.map(conv => (
                              <tr key={conv.sid}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedConversations.includes(conv.sid)}
                                    onChange={() => toggleSelect(conv.sid)}
                                  />
                                </td>
                                <td>
                                  <strong>{conv.friendlyName || 'Unnamed'}</strong>
                                  <br />
                                  <small className="has-text-grey">{conv.sid}</small>
                                </td>
                                <td>
                                  <span className={`tag ${conv.state === 'active' ? 'is-success' : 'is-light'}`}>
                                    {conv.state}
                                  </span>
                                </td>
                                <td>
                                  <small>{formatDate(conv.dateCreated)}</small>
                                </td>
                                <td>
                                  <span className="tag is-light">
                                    {conv.ageInDays} day{conv.ageInDays !== 1 ? 's' : ''}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="button is-small is-danger is-outlined"
                                    onClick={() => deleteConversation(conv.sid)}
                                    disabled={deleting}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>

                {/* Info Box */}
                <div className="notification is-info is-light">
                  <p className="has-text-weight-semibold mb-2">ℹ️ Why delete conversations?</p>
                  <p>
                    When you create a Twilio Conversation, phone numbers become "bound" to that conversation. 
                    This means future messages to those numbers may be routed through the conversation 
                    instead of going directly. Deleting old conversations removes these bindings.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConversationManager;